import React, {useEffect, useState} from "react";
import {Form, Row, Col, Input, InputNumber, Select, Space, Button, DatePicker} from "antd";
import {ColumnGroupType, ColumnType as AntColumnType} from "antd/lib/table/interface";
import {FormInstance} from "antd/lib/form";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";

enum SearchValueType {
    text = 'text',
    select = 'select',
    date = 'date',
    time = 'time',
    dateRange = 'dateRange',
    timeRange = 'timeRange',
    dateTimeRange = 'dateTimeRange',
    digit = 'digit',
}

type SearchValueEnum = {
    text: string,
    value: any,
}[]

interface SearchColumnType {
    search?: boolean
    valueType?: (SearchValueType | string)
    valueInitial?: any,
    valueEnum?: SearchValueEnum,
    valueRender?: JSX.Element
}

export type ColumnType = ((ColumnGroupType<unknown> | AntColumnType<unknown>) & SearchColumnType & { dataIndex: string })

interface SearchBarProps {
    columns: ColumnType[]
    form: FormInstance
    onSubmit: () => void
}

function SearchBar(props: SearchBarProps) {
    const [isCollapse, setCollapse] = useState(true);
    const {columns, form, onSubmit} = props;
    const [colsInRow, setColsInRow] = useState(4);
    const screens = useBreakpoint();
    useEffect(() => {
        if (screens.xxl) {
            setColsInRow(4)
        } else if (screens.xl) {
            setColsInRow(3)
        } else if (screens.lg) {
            setColsInRow(2)
        } else {
            setColsInRow(1)
        }
    }, [screens]);


    function onFormReset() {
        form.resetFields()
    }

    function onFormSubmit() {
        onSubmit()
    }

    function renderItem(item: ColumnType) {
        const {valueType = SearchValueType.text, valueEnum = [], valueRender} = item

        if (valueRender) {
            return valueRender
        }
        if (valueType === SearchValueType.text) {
            return (
                <Input/>
            )
        } else if (valueType === SearchValueType.digit) {
            return (
                <InputNumber/>
            )
        } else if (valueType === SearchValueType.dateRange) {
            return (
                <DatePicker.RangePicker allowClear={false}/>
            )
        } else if (valueType === SearchValueType.select) {
            return (
                <Select allowClear>
                    {valueEnum.map(item => (
                        <Select.Option key={item.value} value={item.value}>{item.text}</Select.Option>
                    ))}
                </Select>
            )
        }
    }

    function renderOperateElement() {
        return (
            <Space>
                <Button onClick={onFormReset}>Reset</Button>
                <Button onClick={onFormSubmit} type={"primary"} htmlType={'submit'}>Search</Button>
                {columns.length > colsInRow - 1 && (
                    <a onClick={() => setCollapse(!isCollapse)}>{isCollapse ? 'Expand' : 'Collapse'}</a>)}
            </Space>
        )
    }

    function renderColumns() {
        const singleColSpan = 24 / colsInRow
        const operateElement = renderOperateElement();
        const total = columns.length;
        const groupColumns: Array<Array<ColumnType | any>> = new Array(Math.ceil((total + 1) / colsInRow)).fill(new Array()).map(arr => [...arr]);
        const operateElementPush = singleColSpan * (colsInRow - 1 - (isCollapse ? Math.min(colsInRow - 1, total) : total) % colsInRow);
        for (let i = 0; i <= total; i++) {
            const rowIndex = Math.floor(i / colsInRow);
            const needHidden = isCollapse && (i + 1 + 1) > colsInRow;
            const column = columns[i];
            const rowClassName = 'SearchBar-Col' + (rowIndex + 1 === groupColumns.length || (isCollapse && rowIndex === 0) ? ' LastRow' : '')
            const item = i === total || (<Form.Item key={column.dataIndex} hidden={needHidden} name={column.dataIndex}
                                                    label={column.title?.toString()}
                                                    initialValue={(column.valueInitial != null && true) ? column.valueInitial : null}>{renderItem(column)}</Form.Item>)
            i !== total ?
                groupColumns[rowIndex].push(needHidden ? item : (
                    <Col className={rowClassName} key={column.dataIndex} span={singleColSpan}>{item}</Col>))
                :
                groupColumns[isCollapse ? 0 : rowIndex].push(<Col key={'op'} span={singleColSpan}
                                                                  style={{textAlign: 'right'}}
                                                                  push={operateElementPush}>{operateElement}</Col>);
        }
        return groupColumns.map((g, index) => (
            <Row key={index} gutter={{xs: 8, sm: 16, md: 24}}>{g}</Row>
        ))
    }

    return (
        <div>
            <Form form={form} labelCol={{span: 8}} wrapperCol={{span: 16}}>
                {renderColumns()}
            </Form>
        </div>
    )
}

export default SearchBar
